import { HttpErrorResponse } from '@angular/common/http';
import { Injectable, Injector, Input } from '@angular/core';
import { AlertController, IonicSafeString } from '@ionic/angular';
import { Store } from '@ngrx/store';
import { EMPTY, from, Observable, of, throwError } from 'rxjs';
import { catchError, filter, mergeMap, switchMap, take, tap } from 'rxjs/operators';

import { RefreshAuthToken } from '../../store/actions';
import { AppState } from '../../store/app-reducers';
import { HttpStatusCodes } from '../enums/HttpStatusCodes';
import { JWTValid } from '../enums/regex';
import { CloseSesion, ErrorServer } from '../models/Constants';
import { IBottleLoading } from '../models/Error';
import { CreceMasService } from '../services/crece-mas.service';
import { MonitoringService } from '../services/monitoring/monitoring.service';
import { SharedServiceService } from '../services/shared-service.service';
import { StorageData } from '../services/utilities/storage-data.class';
import { AppSettings } from './../../app.settings';

/**
 * Servicio que maneja el refresco del token y el cierre forzado de sesión.
 * Es utilizado tanto por el interceptor de HTTP como por servicios de red nativos (ej. RequestService).
 */
@Injectable({ providedIn: 'root' })
export class HandleErrorService {

    @Input() JWT_L$: string = null;
    @Input() isSaveToken = true;

    private alert: HTMLIonAlertElement | null = null;
    private isRefreshing = false;

    constructor(
        private readonly store: Store<AppState>,
        private readonly injector: Injector,
        private alertC: AlertController,
    ) { }

    public getToken$ = (): Observable<string> => this.JWT_L$ ? of(this.JWT_L$) : from(StorageData.getDataStorage(AppSettings.JWT_L));

    /**
     * ! Maneja los errores 401 (Unauthorized) implementando un mecanismo de refresh token:
     * 1. Si no hay un refresh en curso:
     *    - Marca el inicio del proceso de refresh
     *    - Extrae el token actual del request
     *    - Dispara la acción de refresh en el store
     *    - Espera la respuesta del store:
     *      * Si es exitosa: Actualiza el token y reintenta la petición original
     *      * Si falla: Fuerza el cierre de sesión
     * 2. Si ya hay un refresh en curso:
     *    - Espera a que termine el refresh actual
     *    - Reintenta la petición original con el nuevo token
     */
    public handle401Error = <T>(retryCallback: (newToken: string) => Observable<T>): Observable<T> => {
        return this.getToken$().pipe(
            tap(oldToken => { if (!this.isRefreshing) this.store.dispatch(RefreshAuthToken({ token: oldToken })) }),
            mergeMap(() => this.store.select('RefreshAuthToken').pipe(
                tap(state => this.isRefreshing = state.loading),
                filter(state => state.success || state.failed),
                take(1),
                switchMap(state => {
                    // ? El refresh fue exitoso
                    if (state.success) {
                        const newToken = state.token;
                        return retryCallback(newToken);
                    }

                    // ! El refresh falló (no hubo token válido)
                    if (state.failed) {
                        this.presentAlertAccept(ErrorServer);
                        return throwError(() => new Error('Refresh token failed'));
                    }

                    return EMPTY;
                })
            )),
            catchError(err => {
                this.isRefreshing = false;
                return throwError(() => err);
            })
        );
    }

    public handleHttpStatusError = <T>(error: HttpErrorResponse, originalRetry: (newToken: string) => Observable<T>): Observable<T> => {
        switch (error.status) {
            case HttpStatusCodes.UNAUTHORIZED.code:
                return this.handle401Error(originalRetry);
            case HttpStatusCodes.NOT_ACCEPTABLE.code:
                return from(this.forceLogout()).pipe(
                    switchMap(() => throwError(() => new Error(JSON.stringify(HttpStatusCodes.NOT_ACCEPTABLE))))
                );
            default:
                return throwError(() => error);
        }
    }

    public forceLogout = async (): Promise<void> => {
        this.presentAlertAccept(CloseSesion);
        const monitoringService = this.injector.get(MonitoringService);
        const creceMasService = this.injector.get(CreceMasService);
        const sharedServiceService = this.injector.get(SharedServiceService);

        monitoringService.setUser(null);
        await creceMasService.cerrarSesionCreceMas();
        await sharedServiceService.clearData('home');
    }

    public presentAlertAccept = async (loading: IBottleLoading) => {
        if (this.alert !== null) return;

        this.alert = await this.alertC.create({
            animated: true,
            backdropDismiss: loading.showBackdrop,
            keyboardClose: false,
            translucent: true,
            mode: 'ios',
            cssClass: loading.cssClass,
            message: new IonicSafeString(loading.message).value,
            buttons: [
                {
                    text: loading.spinner,
                    handler: () => true
                }
            ]
        });
        await this.alert.present();
        const { data } = await this.alert.onDidDismiss();
        this.alert = null;
        return data;
    }

    /**
     * Validar que un JWT esté correctamente formado y sea un token válido (sin verificar su expiración o firma)
     * token.split('.').length === 3: Verifica si el token tiene exactamente tres partes.
     * token.split('.').every(part => JWTValid.test(part)): Verifica que todas las partes (header, payload, signature) estén en formato Base64URL usando una expresión regular.
     */
    public validarJWT = (token: string): boolean => token.split('.').length === 3 && token.split('.').every(part => JWTValid.test(part));

}
